import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

function formatDateLabel(ymd) {
	if (!ymd) return '';
	const [y, m, d] = ymd.split('-').map(Number);
	if (!y || !m || !d) return ymd;
	const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
	const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thurs', 'Fri', 'Sat'];
	const date = new Date(y, m - 1, d);
	return `${days[date.getDay()]} ${months[m - 1]} ${d}`;
}

function todayYMD() {
	const d = new Date();
	const pad = (n) => String(n).padStart(2, '0');
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function DayCard({ day }) {
	return (
		<div
			style={{
				border: '1px solid #c6ced0',
				borderRadius: 12,
				padding: 12,
				background: 'rgba(255, 255, 255, 0.92)',
				boxShadow: '0 10px 24px rgba(32, 65, 81, 0.12)',
				display: 'grid',
				gap: 10,
			}}
		>
			<div style={{ fontWeight: 800 }}>{formatDateLabel(day.date)}</div>
			<div style={{ display: 'grid', gap: 8 }}>
				<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
					<div style={{ fontWeight: 700, minWidth: 60, whiteSpace: 'nowrap' }}>
						{day.am?.startTime || 'AM'}
					</div>
					<div>
						{day.am ? (
							<span>{day.am.activity || '—'}</span>
						) : (
							'—'
						)}
					</div>
				</div>
				<div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
					<div style={{ fontWeight: 700, minWidth: 60, whiteSpace: 'nowrap' }}>
						{day.pm?.startTime || 'PM'}
					</div>
					<div>
						{day.pm ? (
							<span>{day.pm.activity || '—'}</span>
						) : (
							'—'
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export default function GuideView() {
	const { guideId } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const [guide, setGuide] = useState(null);
	const [itinerary, setItinerary] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');

	const [dateFilter, setDateFilter] = useState(() => {
		const param = searchParams.get('dateRange');
		if (param === 'week') return 'week';
		if (searchParams.has('date')) return 'date';
		return 'today';
	});
	const [customDate, setCustomDate] = useState(() => searchParams.get('date') || todayYMD());
	const [inputDate, setInputDate] = useState(() => searchParams.get('date') || todayYMD());

	// Sync inputDate when customDate changes (e.g., from URL or other updates)
	useEffect(() => {
		setInputDate(customDate);
	}, [customDate]);

	useEffect(() => {
		let cancelled = false;

		async function load() {
			setLoading(true);
			setError('');
			try {
				const params = new URLSearchParams();
				if (dateFilter === 'date' && customDate) {
					params.set('date', customDate);
				} else if (dateFilter === 'today') {
					params.set('dateRange', 'today');
				} else if (dateFilter === 'week') {
					params.set('dateRange', 'week');
				}

				const [guideRes, itinRes] = await Promise.all([
					fetch(`/api/guides/${guideId}`),
					fetch(`/api/guides/${guideId}/itinerary?${params.toString()}`),
				]);

				if (!guideRes.ok || !itinRes.ok) {
					throw new Error('Unable to load guide schedule.');
				}

				const guideJson = await guideRes.json();
				const itinJson = await itinRes.json();
				if (!cancelled) {
					setGuide(guideJson);
					setItinerary(itinJson);
				}
			} catch (e) {
				if (!cancelled) setError(e.message || 'Unable to load guide schedule.');
			} finally {
				if (!cancelled) setLoading(false);
			}
		}

		if (guideId) load();

		return () => {
			cancelled = true;
		};
	}, [guideId, dateFilter, customDate]);

	function handleDateFilterChange(newFilter) {
		setDateFilter(newFilter);
		// Update URL params
		const params = new URLSearchParams();
		if (newFilter === 'date' && customDate) {
			params.set('date', customDate);
		} else if (newFilter === 'week') {
			params.set('dateRange', 'week');
		} else if (newFilter === 'today') {
			params.set('dateRange', 'today');
		}
		setSearchParams(params);
	}

	function handleDateChange(newDate) {
		setCustomDate(newDate);
		const params = new URLSearchParams();
		params.set('date', newDate);
		setSearchParams(params);
	}

	return (
		<div style={{ maxWidth: 720, margin: '0 auto', padding: 16, minHeight: '100vh', background: 'var(--page-bg)' }}>
			<style>{`
        .guide-days-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .guide-day-card {
          break-inside: avoid;
          page-break-inside: avoid;
        }
        @media (max-width: 640px) {
          .guide-days-grid {
            grid-template-columns: 1fr;
          }
        }
        @media print {
          .guide-days-grid {
            grid-template-columns: 1fr 1fr;
          }
          .no-print {
            display: none;
          }
        }
      `}</style>
			{loading && <div>Loading schedule…</div>}
			{error && <div style={{ color: 'crimson' }}>{error}</div>}

			{!loading && !error && guide && itinerary && (
				<div style={{ display: 'grid', gap: 16 }}>
					<div style={{ border: '1px solid #ddd', borderRadius: 12, padding: 16 }}>
						<div style={{ fontSize: 20, fontWeight: 800 }}>{guide.name || 'Guide'}</div>
						<div style={{ marginTop: 6, opacity: 0.8 }}>
							{itinerary.dateRange?.start ? `From ${formatDateLabel(itinerary.dateRange.start)}` : ''}
							{itinerary.dateRange?.start && itinerary.dateRange?.end ? ' • ' : ''}
							{itinerary.dateRange?.end ? `To ${formatDateLabel(itinerary.dateRange.end)}` : ''}
						</div>
						<div style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>
							Guide schedule
						</div>
					</div>

					{/* Date filter controls */}
					<div
						className="no-print"
						style={{ border: '1px solid #ddd', borderRadius: 12, padding: 12 }}
					>
						<div style={{ fontWeight: 700, marginBottom: 8 }}>View Schedule</div>
						<div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
							<select
								value={dateFilter}
								onChange={(e) => handleDateFilterChange(e.target.value)}
								style={{
									padding: 8,
									borderRadius: 8,
									border: '1px solid #ccc',
									background: 'var(--card)',
									fontFamily: 'inherit',
									fontSize: 13,
								}}
							>
								<option value="today">Today</option>
								<option value="week">This Week</option>
								<option value="date">Specific Date</option>
							</select>
							{dateFilter === 'date' && (
								<input
									type="date"
									value={inputDate}
									onChange={(e) => setInputDate(e.target.value)}
									onBlur={(e) => {
										if (e.target.value && e.target.value !== customDate) {
											handleDateChange(e.target.value);
										}
									}}
									style={{
										padding: 8,
										borderRadius: 8,
										border: '1px solid #ccc',
										background: 'var(--card)',
										fontFamily: 'inherit',
										fontSize: 13,
									}}
								/>
							)}
						</div>
					</div>

					{/* Itinerary */}
					{itinerary.days?.length ? (
						<div className="guide-days-grid">
							{itinerary.days.map((day) => (
								<div key={day.date} className="guide-day-card">
									<DayCard day={day} />
								</div>
							))}
						</div>
					) : (
						<div style={{ opacity: 0.7, padding: 16, textAlign: 'center' }}>
							No scheduled activities for this period.
						</div>
					)}
				</div>
			)}
		</div>
	);
}
